import { useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { WEEKDAYS } from "@/lib/catalog";
import { field, Field, Panel, useAdminSettings } from "@/components/admin/shared";

const WeekEditor = ({ hours, setHours }) => (
  <div className="space-y-3">
    {WEEKDAYS.map((d, i) => {
      const h = hours[String(i)];
      const upd = (p) => setHours({ ...hours, [String(i)]: { ...h, ...p } });
      return (
        <div key={d} className="grid grid-cols-[110px,auto,1fr,1fr] items-center gap-3" data-testid={`hours-row-${i}`}>
          <span className="font-semibold">{d}</span>
          <Switch checked={h.enabled} onCheckedChange={(v) => upd({ enabled: v })} aria-label={`Abrir ${d}`} data-testid={`hours-enabled-${i}`} />
          <input type="time" aria-label={`Apertura ${d}`} disabled={!h.enabled} className={field} value={h.open} onChange={(e) => upd({ open: e.target.value })} data-testid={`hours-open-${i}`} />
          <input type="time" aria-label={`Cierre ${d}`} disabled={!h.enabled} className={field} value={h.close} onChange={(e) => upd({ close: e.target.value })} data-testid={`hours-close-${i}`} />
        </div>
      );
    })}
  </div>
);

const BookingRules = ({ b, setB }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    <Field id="booking-slot-minutes" label="Duración de cada bloque (min)" type="number" min={15} step={15} value={b.slot_minutes} onChange={(e) => setB({ ...b, slot_minutes: Number(e.target.value) })} />
    <Field id="booking-capacity" label="Citas por bloque" type="number" min={1} value={b.capacity} onChange={(e) => setB({ ...b, capacity: Number(e.target.value) })} />
    <Field id="booking-notice" label="Anticipación mínima (horas)" type="number" min={0} value={b.min_notice_hours} onChange={(e) => setB({ ...b, min_notice_hours: Number(e.target.value) })} />
    <Field id="booking-max-days" label="Reservar hasta (días adelante)" type="number" min={1} value={b.max_days_ahead} onChange={(e) => setB({ ...b, max_days_ahead: Number(e.target.value) })} />
    <label className="flex items-center gap-3 text-sm sm:col-span-2">
      <Switch checked={b.auto_confirm} onCheckedChange={(v) => setB({ ...b, auto_confirm: v })} data-testid="booking-auto-confirm" />
      Confirmar automáticamente las citas del sitio (si se desactiva quedan "pendientes")
    </label>
  </div>
);

const Blocked = ({ list, setList }) => {
  const [d, setD] = useState({ date: "", reason: "" });
  return (
    <div>
      <div className="flex flex-wrap gap-3">
        <input type="date" aria-label="Fecha bloqueada" className={`${field} max-w-[180px]`} value={d.date} onChange={(e) => setD({ ...d, date: e.target.value })} data-testid="blocked-date-input" />
        <input placeholder="Motivo (ej. día festivo)" aria-label="Motivo" className={`${field} flex-1 min-w-[160px]`} value={d.reason} onChange={(e) => setD({ ...d, reason: e.target.value })} data-testid="blocked-reason-input" />
        <button type="button" className="btn-ghost !py-2" disabled={!d.date} onClick={() => { setList([...list.filter((x) => x.date !== d.date), d]); setD({ date: "", reason: "" }); }} data-testid="blocked-add-btn"><Plus size={16} /> Bloquear</button>
      </div>
      <ul className="mt-4 space-y-2">
        {list.map((x) => (
          <li key={x.date} className="flex items-center justify-between rounded-sm border border-line px-3 py-2 text-sm" data-testid={`blocked-${x.date}`}>
            <span><span className="font-mono text-brand">{x.date}</span> · {x.reason || "Sin motivo"}</span>
            <button type="button" aria-label="Quitar" onClick={() => setList(list.filter((y) => y.date !== x.date))} className="text-red-400"><Trash2 size={15} /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const Editor = ({ settings, save }) => {
  const [hours, setHours] = useState(settings.hours);
  const [booking, setBooking] = useState(settings.booking);
  const [blocked, setBlocked] = useState(settings.blocked_dates);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Panel title="Horario semanal"><WeekEditor hours={hours} setHours={setHours} /></Panel>
      <div className="space-y-6">
        <Panel title="Reglas de reservación"><BookingRules b={booking} setB={setBooking} /></Panel>
        <Panel title="Días bloqueados"><Blocked list={blocked} setList={setBlocked} /></Panel>
      </div>
      <div className="lg:col-span-2">
        <button className="btn-gold" onClick={() => save({ hours, booking, blocked_dates: blocked })} data-testid="hours-save-btn">Guardar horarios y disponibilidad</button>
      </div>
    </div>
  );
};

export const HoursTab = () => {
  const { settings, save } = useAdminSettings();
  if (!settings) return <Loader2 className="animate-spin text-brand" />;
  return <Editor settings={settings} save={save} />;
};
